import { useState, useEffect } from "react";
import api from "../../utils/api";
import ClickTrendsChart from "./ClickTrendsChart";
import GeoBreakdownChart from "./GeoBreakdownChart";
import DeviceBreakdownChart from "./DeviceBreakdownChart";
import DomainChart from "./DomainChart";
import ScansByProductGrid from "./ScansByProductGrid";
import ScanMixChart from "./ScanMixChart";
import TransitionChart from "./TransitionChart";
import LaunchPerformanceGrid from "./LaunchPerformanceGrid";
import DeprecatedShareGrid from "./DeprecatedShareGrid";
import NotFoundTable from "./NotFoundTable";
import GeoDetailChart from "./GeoDetailChart";
import TimePatternsChart from "./TimePatternsChart";

const AnalyticsDashboard = () => {
  // Default to rolling 90 days
  const defaultStart = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [filters, setFilters] = useState({
    startDate: defaultStart,
    endDate: "",
    productId: "",
    retailer: "",
    groupBy: "day",
  });
  const [products, setProducts] = useState([]);
  const [options, setOptions] = useState({ retailers: [] });
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/api/products").then((res) => {
      setProducts(res.data);
      const totalClicks = res.data.reduce((s, p) => s + p.totalClicks, 0);
      const totalCodes = res.data.reduce((s, p) => s + p.urls.length, 0);
      const topProduct = res.data[0];
      setSummary({ totalClicks, totalCodes, topProduct });
    }).catch(console.error);

    api.get("/api/options").then((res) => setOptions(res.data)).catch(console.error);
  }, []);

  const handleFilter = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // Build query string from filters
  const queryParams = Object.entries(filters)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");

  return (
    <div>
      {/* Summary Cards */}
      {summary && (
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h3 className="text-primary">{summary.totalClicks.toLocaleString()}</h3>
                <small className="text-muted">Total Scans</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h3 className="text-success">{summary.totalCodes}</h3>
                <small className="text-muted">Active QR Codes</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h3 className="text-info">{products.length}</h3>
                <small className="text-muted">Products</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="text-warning mb-0">
                  {summary.topProduct?.productName || "—"}
                </h5>
                <small className="text-muted">
                  Top Product ({summary.topProduct?.totalClicks.toLocaleString()} scans)
                </small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-2">
              <label className="form-label small">Start Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.startDate}
                onChange={(e) => handleFilter("startDate", e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small">End Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.endDate}
                onChange={(e) => handleFilter("endDate", e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small">Product</label>
              <select
                className="form-select form-select-sm"
                value={filters.productId}
                onChange={(e) => handleFilter("productId", e.target.value)}
              >
                <option value="">All Products</option>
                {products.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.productName} {p.upc ? `(${p.upc})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small">Retailer</label>
              <select
                className="form-select form-select-sm"
                value={filters.retailer}
                onChange={(e) => handleFilter("retailer", e.target.value)}
              >
                <option value="">All</option>
                {options.retailers?.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small">Granularity</label>
              <select
                className="form-select form-select-sm"
                value={filters.groupBy}
                onChange={(e) => handleFilter("groupBy", e.target.value)}
              >
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
              </select>
            </div>
            <div className="col-md-1">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() =>
                  setFilters({ startDate: defaultStart, endDate: "", productId: "", retailer: "", groupBy: "day" })
                }
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Click Trends */}
      <div className="card mb-4">
        <div className="card-header">Scan Trends</div>
        <div className="card-body">
          <ClickTrendsChart queryParams={queryParams} />
        </div>
      </div>

      {/* Geo + Devices + Scan mix */}
      <div className="row">
        <div className="col-md-4">
          <div className="card mb-4">
            <div className="card-header">Geographic Breakdown</div>
            <div className="card-body">
              <GeoBreakdownChart queryParams={queryParams} />
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card mb-4">
            <div className="card-header">Device & Browser</div>
            <div className="card-body">
              <DeviceBreakdownChart queryParams={queryParams} />
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card mb-4">
            <div className="card-header">Scan Mix</div>
            <div className="card-body">
              <ScanMixChart queryParams={queryParams} />
            </div>
          </div>
        </div>
      </div>

      {/* Packaging transition: legacy -> GS1 over time */}
      <div className="card mb-4">
        <div className="card-header">Packaging Transition</div>
        <div className="card-body">
          <TransitionChart queryParams={queryParams} />
        </div>
      </div>

      {/* Time patterns */}
      <div className="card mb-4">
        <div className="card-header">Scan Timing</div>
        <div className="card-body">
          <TimePatternsChart queryParams={queryParams} />
        </div>
      </div>

      {/* Geo detail */}
      <div className="card mb-4">
        <div className="card-header">Regional Breakdown</div>
        <div className="card-body">
          <GeoDetailChart queryParams={queryParams} />
        </div>
      </div>

      {/* Domain breakdown */}
      <div className="card mb-4">
        <div className="card-header">Scans by Domain</div>
        <div className="card-body">
          <DomainChart queryParams={queryParams} />
        </div>
      </div>

      {/* Scans by product, pivoted by side, for the selected window */}
      <div className="card mb-4">
        <div className="card-header">Scans by Product</div>
        <div className="card-body">
          <ScansByProductGrid queryParams={queryParams} startDate={filters.startDate} endDate={filters.endDate} />
        </div>
      </div>

      {/* Launch performance + residual scans on retired codes */}
      <div className="row">
        <div className="col-md-6">
          <div className="card mb-4">
            <div className="card-header">Launch Performance</div>
            <div className="card-body">
              <LaunchPerformanceGrid queryParams={queryParams} />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card mb-4">
            <div className="card-header">Scans on Retired Codes</div>
            <div className="card-body">
              <DeprecatedShareGrid queryParams={queryParams} />
            </div>
          </div>
        </div>
      </div>

      {/* Dead-end scans */}
      <div className="card mb-4">
        <div className="card-header">Not-Found Scans</div>
        <div className="card-body">
          <NotFoundTable queryParams={queryParams} />
        </div>
      </div>

    </div>
  );
};

export default AnalyticsDashboard;
